/**
 * DJ1835Page — Rentas de arrendamiento percibidas por bienes raíces.
 *
 * Cruza los SaleDocuments aceptados del año con FK `real_estate`. Para que un
 * documento aparezca, debe tener un bien raíz asociado al emitirse.
 */
import { useMemo, useState } from 'react'
import { Download, Eye, Info, AlertCircle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

import apiClient from '@/lib/apiClient'
import DownloadButton from '@/components/DownloadButton'
import type { DJ1835Preview } from '../hooks/useRealEstate'

function fmtCLP(value: string | number) {
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n)) return '—'
  return n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })
}

export function DJ1835Page() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear - 1)
  const [preview, setPreview] = useState<DJ1835Preview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const years = useMemo(
    () => Array.from({ length: 8 }, (_, i) => currentYear - i),
    [currentYear],
  )

  const onPreview = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await apiClient.get<DJ1835Preview>(`/real-estate/dj-1835/${year}/preview/`)
      setPreview(data)
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Error al cargar previsualización')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-content">DJ 1835 — Bienes Raíces Arrendados</h1>
          <p className="text-sm text-content-subtle mt-1">
            Rentas de arrendamiento percibidas durante el año comercial, agrupadas
            por inmueble (ROL avalúo SII) y por arrendatario.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label htmlFor="dj1835page-ano-comercial" className="text-sm text-content-muted">Año comercial</label>
          <select id="dj1835page-ano-comercial"
            value={year}
            onChange={(e) => { setYear(parseInt(e.target.value)); setPreview(null) }}
            className="px-3 py-2 text-sm border border-divider rounded-lg bg-surface text-content"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            type="button"
            onClick={onPreview}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-divider rounded-lg hover:bg-surface-hover disabled:opacity-50"
          >
            <Eye size={14} /> {loading ? 'Cargando...' : 'Previsualizar'}
          </button>
          <DownloadButton path={`/real-estate/dj-1835/${year}/export/`} filename={`DJ1835_${year}.csv`} />
        </div>
      </div>

      <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-4 text-sm text-content-subtle flex gap-3 shadow-card">
        <Info size={16} className="shrink-0 mt-0.5 text-primary" />
        <div className="space-y-1">
          <p>
            Para que un documento aparezca acá, debe tener asociado un{' '}
            <a href="/accounting/real-estate" className="text-primary hover:underline">Bien Raíz</a>
            {' '}al emitirse. Solo se consideran documentos en estado <strong>aceptado</strong>.
          </p>
          <p className="text-xs">Las Notas de Crédito (NC) restan del total anual del inmueble + arrendatario.</p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger bg-danger/10 p-4 text-sm text-danger flex gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {preview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard label="Registros" value={String(preview.totals.count)} />
            <StatCard label="Total Rentas" value={fmtCLP(preview.totals.rent_total)} />
            <StatCard label="Año" value={String(preview.year)} />
          </div>

          {preview.items.length === 0 ? (
            <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-8 text-center text-content-muted shadow-card">
              No se detectaron rentas para el año {preview.year}. Verifica que existan
              documentos aceptados con bien raíz asociado.
            </div>
          ) : (
            <DataTable
              columns={[
                { key: 'rol_avaluo', header: 'ROL Avalúo', render: it => <span className="font-mono">{it.rol_avaluo}</span> },
                { key: 'address', header: 'Dirección', render: it => <span className="text-content-subtle">{it.address}</span> },
                { key: 'comuna', header: 'Comuna', render: it => <span className="text-content-subtle">{it.comuna}</span> },
                { key: 'tenant_tax_id', header: 'Arrendatario', render: it => it.tenant_tax_id || '—' },
                { key: 'document_count', header: 'Docs', numeric: true },
                { key: 'rent_total', header: 'Renta Anual', numeric: true, render: it => fmtCLP(it.rent_total) },
              ]}
              rows={preview.items}
              rowKey={(_, i) => i}
              minWidth={760}
              caption="Rentas de bienes raíces del año declarado"
            />
          )}
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-4 shadow-card">
      <div className="text-xs text-content-muted">{label}</div>
      <div className="text-lg font-bold text-content mt-1 tabular-nums">{value}</div>
    </div>
  )
}

export default DJ1835Page
